/* spirograph/index.js — DOM orchestrator for the Spirograph tool.
 *
 * The only DOM-coupled module. The rAF loop runs ONLY while the progressive
 * draw animation is playing; a finished curve is a single static render.
 */

import { reduce } from './curve.js';
import { allPresets, presetToParams, randomCurve } from './presets.js';
import {
    defaultComposition, cloneComposition, addLayer, duplicateLayer, removeLayer,
    createHistory, pushHistory, undo, redo, encodeHash, decodeHash, MAX_LAYERS,
} from './composition.js';
import { sampleLayers, sharedTransform, drawComposition } from './render.js';
import { compositionToSvg } from './svg-export.js';
import { dpr, reducedMotion } from '../toy-fx/prefs.js';
import { initFunnel } from '../toy-fx/telemetry.js';
import { exportPng, svgToBlob, downloadBlob } from '../toy-fx/canvas-export.js';

const SLUG = 'spirograph';
const I18N = (typeof window !== 'undefined' && window.SpirographI18n) || {};
const t = (k, f) => (I18N && I18N[k]) || f || k;
const $ = (id) => document.getElementById(id);
function ae(ev, p) { try { if (window.__ae) window.__ae(ev, Object.assign({ slug: SLUG }, p || {})); } catch (_) {} }

export function mount() {
    const stage = $('sp-stage');
    const canvas = $('sp-canvas');
    if (!stage || !canvas) return;
    const ctx = canvas.getContext('2d');

    const state = {
        comp: defaultComposition(),
        history: createHistory(),
        sampled: null,
        tr: null,
        animating: false,
        animProgress: 1,
        animStart: 0,
        animSpeed: 1,
    };
    let rafId = 0, w = 0, h = 0;

    /* ---- settings ---- */
    function restore() {
        try {
            const ts = window.__toolSettings; if (!ts) return;
            const saved = ts.load(SLUG, 'comp', '');
            if (saved) {
                const c = decodeHash(saved);
                if (c) state.comp = c;
            }
        } catch (_) {}
    }
    function persist() {
        try { window.__toolSettings && window.__toolSettings.save(SLUG, 'comp', encodeHash(state.comp)); } catch (_) {}
    }
    function loadHash() {
        const m = /[#&]s=([^&]+)/.exec(location.hash || '');
        if (!m) return false;
        const c = decodeHash(decodeURIComponent(m[1]));
        if (c) { state.comp = c; return true; }
        return false;
    }

    /* ---- geometry ---- */
    function resize() {
        const rect = stage.getBoundingClientRect();
        const size = Math.max(200, Math.min(rect.width, rect.height || rect.width));
        const r = dpr();
        canvas.width = Math.round(size * r);
        canvas.height = Math.round(size * r);
        canvas.style.width = size + 'px';
        canvas.style.height = size + 'px';
        ctx.setTransform(r, 0, 0, r, 0, 0);
        w = size; h = size;
        resample();
        render();
    }

    function resample() {
        state.sampled = sampleLayers(state.comp);
        state.tr = sharedTransform(state.sampled, w, h);
    }

    function render() {
        try {
            drawComposition(ctx, state.comp, state.sampled, state.tr, w, h,
                state.animating ? state.animProgress : 1);
        } catch (e) {
            if (window.__handleError) window.__handleError(e, 'spirograph.render');
        }
        updateInfo();
    }

    function updateInfo() {
        const el = $('sp-info');
        if (!el) return;
        const l = activeLayer();
        const rd = reduce(l.R, l.r);
        el.textContent = t('info', '{p} petals · {n} layers')
            .replace('{p}', rd.petals).replace('{n}', state.comp.layers.length);
        canvas.setAttribute('aria-label',
            t('canvas_aria', 'Spirograph curve with {p} petals').replace('{p}', rd.petals));
    }

    function activeLayer() { return state.comp.layers[state.comp.active] || state.comp.layers[0]; }

    /* ---- animation ---- */
    function startAnimation() {
        if (reducedMotion()) { state.animProgress = 1; state.animating = false; render(); return; }
        state.animating = true;
        state.animProgress = 0;
        state.animStart = performance.now();
        ensureLoop();
        ae('feature_use', { f: 'animate_play' });
    }
    function ensureLoop() {
        if (rafId) return;
        rafId = requestAnimationFrame(frame);
    }
    function frame(now) {
        rafId = 0;
        try {
            const dur = 4200 / Math.max(0.25, state.animSpeed);
            state.animProgress = Math.min(1, (now - state.animStart) / dur);
            render();
            if (state.animProgress >= 1) { state.animating = false; }
            else rafId = requestAnimationFrame(frame);
        } catch (e) {
            if (window.__handleError) window.__handleError(e, 'spirograph.frame');
        }
    }

    /* ---- mutation ---- */
    let histTimer = 0;
    function snapshotSoon() {
        if (histTimer) return;
        const before = cloneComposition(state.comp);
        histTimer = setTimeout(() => {
            histTimer = 0;
            pushHistory(state.history, before);
            refreshHistoryButtons();
        }, 400);
    }
    function commit(resampleNeeded) {
        if (resampleNeeded !== false) resample();
        render();
        persist();
        syncControls();
    }

    /* ---- controls ---- */
    function syncControls() {
        const l = activeLayer();
        setVal('sp-R', Math.round(l.R));
        setVal('sp-r', Math.round(l.r));
        setVal('sp-d', Math.round(l.d));
        setVal('sp-rotation', Math.round((l.rotation || 0) * 57.2958));
        setVal('sp-thickness', l.thickness);
        setVal('sp-type', l.type);
        setVal('sp-pen', l.penStyle);
        setVal('sp-color', l.color);
        setVal('sp-bg', state.comp.background);
        setVal('sp-bgstyle', state.comp.bgStyle);
        renderLayers();
    }
    function setVal(id, v) { const el = $(id); if (el && el.value !== String(v)) el.value = v; }

    function renderLayers() {
        const host = $('sp-layers');
        if (!host) return;
        host.innerHTML = '';
        state.comp.layers.forEach((layer, i) => {
            const chip = document.createElement('div');
            chip.className = 'sp-layer' + (i === state.comp.active ? ' sp-layer-active' : '');
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'sp-layer-pick';
            dot.style.background = layer.color;
            dot.setAttribute('aria-label', t('select_layer', 'Select layer {n}').replace('{n}', i + 1));
            dot.addEventListener('click', () => { state.comp.active = i; commit(false); });
            const name = document.createElement('span');
            name.className = 'sp-layer-name';
            name.textContent = t('layer', 'Layer {n}').replace('{n}', i + 1);
            name.addEventListener('click', () => { state.comp.active = i; commit(false); });
            const vis = document.createElement('button');
            vis.type = 'button';
            vis.className = 'sp-layer-btn';
            vis.textContent = layer.visible ? '👁' : '🚫';
            vis.addEventListener('click', () => { snapshotSoon(); layer.visible = !layer.visible; commit(); });
            const del = document.createElement('button');
            del.type = 'button';
            del.className = 'sp-layer-btn';
            del.textContent = '✕';
            del.disabled = state.comp.layers.length <= 1;
            del.addEventListener('click', () => {
                snapshotSoon();
                if (removeLayer(state.comp, i)) commit();
            });
            chip.appendChild(dot);
            chip.appendChild(name);
            chip.appendChild(vis);
            chip.appendChild(del);
            host.appendChild(chip);
        });
        const addBtn = $('sp-add-layer');
        if (addBtn) addBtn.disabled = state.comp.layers.length >= MAX_LAYERS;
    }

    function refreshHistoryButtons() {
        const u = $('sp-undo'), r = $('sp-redo');
        if (u) u.disabled = state.history.past.length === 0;
        if (r) r.disabled = state.history.future.length === 0;
    }

    /* ---- exports ---- */
    function exportPNG() {
        const scale = parseInt(($('sp-scale') || {}).value || '2', 10);
        exportPng((c, cw, ch) => {
            const sampled = sampleLayers(state.comp);
            const tr = sharedTransform(sampled, cw, ch);
            drawComposition(c, state.comp, sampled, tr, cw, ch, 1);
        }, {
            w: 1000, h: 1000, scale: scale,
            transparent: state.comp.bgStyle === 'transparent',
            bg: state.comp.background,
        }).then((blob) => {
            downloadBlob(blob, 'spirograph.png');
            ae('feature_use', { f: 'export', format: 'png', scale: scale });
            try { window.__aeToolComplete && window.__aeToolComplete(); } catch (_) {}
        }).catch((e) => { if (window.__handleError) window.__handleError(e, 'spirograph.png'); });
    }
    function exportSVG() {
        try {
            const svg = compositionToSvg(state.comp, 1000);
            downloadBlob(svgToBlob(svg), 'spirograph.svg');
            ae('feature_use', { f: 'export', format: 'svg' });
            try { window.__aeToolComplete && window.__aeToolComplete(); } catch (_) {}
        } catch (e) { if (window.__handleError) window.__handleError(e, 'spirograph.svg'); }
    }
    function copyShare() {
        const url = location.origin + location.pathname + '#s=' + encodeURIComponent(encodeHash(state.comp));
        const done = () => { if (window.__toast) window.__toast(t('link_copied', 'Share link copied'), 'success'); };
        if (navigator.clipboard) navigator.clipboard.writeText(url).then(done, () => prompt(t('share_prompt', 'Copy:'), url));
        else prompt(t('share_prompt', 'Copy:'), url);
        history.replaceState(null, '', '#s=' + encodeURIComponent(encodeHash(state.comp)));
        ae('feature_use', { f: 'share' });
    }

    /* ---- wiring ---- */
    function wire() {
        bindLayerRange('sp-R', 'R');
        bindLayerRange('sp-r', 'r');
        bindLayerRange('sp-d', 'd');
        bindLayerRange('sp-thickness', 'thickness');
        const rot = $('sp-rotation');
        if (rot) rot.addEventListener('input', () => {
            snapshotSoon();
            activeLayer().rotation = (parseFloat(rot.value) || 0) / 57.2958;
            commit();
        });
        bindLayerSelect('sp-type', 'type');
        bindLayerSelect('sp-pen', 'penStyle');
        const color = $('sp-color');
        if (color) color.addEventListener('input', () => {
            snapshotSoon(); activeLayer().color = color.value; commit(false); render();
        });
        const bg = $('sp-bg');
        if (bg) bg.addEventListener('input', () => { snapshotSoon(); state.comp.background = bg.value; commit(false); render(); });
        bindCompSelect('sp-bgstyle', 'bgStyle');

        const preset = $('sp-preset');
        if (preset) {
            allPresets().forEach((p, i) => {
                const o = document.createElement('option');
                o.value = i;
                o.textContent = `${p.ringTeeth} / ${p.gearTeeth}`;
                preset.appendChild(o);
            });
            preset.addEventListener('change', () => {
                const p = allPresets()[parseInt(preset.value, 10)];
                if (!p) return;
                snapshotSoon();
                const params = presetToParams(p.ringTeeth, p.gearTeeth);
                Object.assign(activeLayer(), { type: params.type, R: params.R, r: params.r, d: params.d });
                commit();
                ae('feature_use', { f: 'preset_select' });
            });
        }

        bindClick('sp-add-layer', () => {
            snapshotSoon();
            if (addLayer(state.comp)) { commit(); ae('feature_use', { f: 'layer_add' }); }
        });
        bindClick('sp-dup-layer', () => {
            snapshotSoon();
            if (duplicateLayer(state.comp)) { commit(); ae('feature_use', { f: 'layer_duplicate' }); }
        });
        bindClick('sp-random', () => {
            snapshotSoon();
            const c = randomCurve(Math.random);
            Object.assign(activeLayer(), { type: c.type, R: c.R, r: c.r, d: c.d });
            commit();
            ae('feature_use', { f: 'randomize' });
        });
        bindClick('sp-animate', startAnimation);
        bindClick('sp-undo', () => {
            const c = undo(state.history, state.comp);
            if (c) { state.comp = c; commit(); refreshHistoryButtons(); }
        });
        bindClick('sp-redo', () => {
            const c = redo(state.history, state.comp);
            if (c) { state.comp = c; commit(); refreshHistoryButtons(); }
        });
        bindClick('sp-clear', () => {
            snapshotSoon();
            state.comp = defaultComposition();
            commit();
        });
        bindClick('sp-png', exportPNG);
        bindClick('sp-svg', exportSVG);
        bindClick('sp-share', copyShare);

        const speed = $('sp-speed');
        if (speed) speed.addEventListener('input', () => { state.animSpeed = parseFloat(speed.value) || 1; });

        window.addEventListener('resize', debounce(resize, 150));
        window.addEventListener('beforeunload', () => { if (rafId) cancelAnimationFrame(rafId); });
    }
    function bindClick(id, fn) { const el = $(id); if (el) el.addEventListener('click', fn); }
    function bindLayerRange(id, key) {
        const el = $(id); if (!el) return;
        el.addEventListener('input', () => {
            snapshotSoon();
            activeLayer()[key] = parseFloat(el.value) || 0;
            commit();
        });
    }
    function bindLayerSelect(id, key) {
        const el = $(id); if (!el) return;
        el.addEventListener('change', () => { snapshotSoon(); activeLayer()[key] = el.value; commit(); });
    }
    function bindCompSelect(id, key) {
        const el = $(id); if (!el) return;
        el.addEventListener('change', () => { snapshotSoon(); state.comp[key] = el.value; commit(false); render(); });
    }

    /* ---- boot ---- */
    restore();
    const fromHash = loadHash();
    wire();
    resize();
    syncControls();
    refreshHistoryButtons();
    if (fromHash && window.__toast) window.__toast(t('preset_loaded', 'Shared design loaded'), 'info');
    try { initFunnel(SLUG); } catch (_) {}

    window.__spiroDebug = {
        state: state,
        layerCount: () => state.comp.layers.length,
        addLayer: () => { addLayer(state.comp); commit(); },
        setActive: (k, v) => { activeLayer()[k] = v; commit(); },
        exportSvg: () => compositionToSvg(state.comp, 1000),
    };
}

function debounce(fn, ms) {
    let timer = 0;
    return function () { clearTimeout(timer); timer = setTimeout(fn, ms); };
}

if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount);
    else mount();
}
